// TipTap extension for Tab-triggered LaTeX snippets
// Detects snippet triggers (scn, oo, pf, etc.) before cursor and expands on Tab

import { Extension } from "@tiptap/core";
import type { Editor } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";

export interface SnippetDefinition {
    trigger: string;
    handler: (editor: Editor) => boolean;
    // For regex-based triggers
    isRegex?: boolean;
    regexPattern?: string;
    dynamicHandler?: (editor: Editor, captures: string[]) => boolean;
}

// Plugin key for accessing the current-word state from PaperEditor
export const snippetHintPluginKey = new PluginKey<{ currentWord: string }>("snippetHint");

// ── Exported helpers so replay / hint paths call the exact same logic ────────

export function runHeadingEnterBehavior(editor: Editor): boolean {
    const { $from } = editor.state.selection;
    if ($from.parent.type.name !== "heading") return false;
    const endOfHeading = $from.end();
    editor
        .chain()
        .setTextSelection(endOfHeading)
        .insertContent({ type: "paragraph" })
        .focus()
        .run();
    return true;
}

export function runSnippetTabBehavior(editor: Editor, snippets: SnippetDefinition[]): boolean {
    const { state } = editor.view;
    const { $from } = state.selection;

    if ($from.parent.type.name === "codeBlock") {
        const end = $from.after($from.depth);
        editor.chain().setTextSelection(end).insertContent({ type: "paragraph" }).focus().run();
        return true;
    }

    if ($from.parent.type.name === "heading") {
        const endOfHeading = $from.end();
        editor
            .chain()
            .setTextSelection(endOfHeading)
            .insertContent({ type: "paragraph" })
            .focus()
            .run();
        return true;
    }

    if (editor.isActive("code")) {
        editor.chain().unsetMark("code").focus().run();
        return true;
    }

    const textBefore = $from.parent.textBetween(
        Math.max(0, $from.parentOffset - 20),
        $from.parentOffset,
        undefined,
        "\ufffc"
    );

    for (const snippet of snippets) {
        if (snippet.isRegex && snippet.regexPattern && snippet.dynamicHandler) {
            const regex = new RegExp(`(${snippet.regexPattern})$`);
            const match = textBefore.match(regex);
            if (match) {
                const captures = match.slice(2);
                const handled = snippet.dynamicHandler(editor, captures);
                if (handled) return true;
            }
        } else if (textBefore.endsWith(snippet.trigger)) {
            const handled = snippet.handler(editor);
            if (handled) return true;
        }
    }

    // No snippet matched — if inside a block environment, Tab exits it.
    const ENV_NODES = new Set(["theoremBlock", "genericEnv", "twoColumns", "docAbstract"]);
    for (let d = $from.depth; d >= 1; d--) {
        if (ENV_NODES.has($from.node(d).type.name)) {
            const afterPos = $from.after(d);
            const clampedPos = Math.min(afterPos, state.doc.content.size);
            const $afterPos = state.doc.resolve(clampedPos);
            if ($afterPos.parentOffset === $afterPos.parent.content.size) {
                editor.chain()
                    .insertContentAt(afterPos, { type: "paragraph" })
                    .setTextSelection(afterPos + 1)
                    .run();
            } else {
                // afterPos sits between two block nodes at the parent level — a
                // position ProseMirror won't accept as a TextSelection endpoint.
                // TextSelection.near scans forward to the first valid inline pos.
                const $near = editor.state.doc.resolve(afterPos);
                editor.view.dispatch(
                    editor.state.tr.setSelection(TextSelection.near($near, 1))
                );
                editor.view.focus();
            }
            return true;
        }
    }

    return false;
}

export const SnippetExtension = Extension.create({
    name: "snippets",

    addOptions() {
        return {
            snippets: [] as SnippetDefinition[],
            onCurrentWordChange: undefined as ((word: string) => void) | undefined,
        };
    },

    addProseMirrorPlugins() {
        const onWordChange = this.options.onCurrentWordChange;
        return [
            new Plugin({
                key: snippetHintPluginKey,
                view() {
                    let lastWord = "";
                    return {
                        update(view) {
                            if (!onWordChange) return;
                            const { state } = view;
                            const { $from } = state.selection;
                            // Extract current word (contiguous non-space chars before cursor)
                            const textBefore = $from.parent.textBetween(
                                Math.max(0, $from.parentOffset - 20),
                                $from.parentOffset,
                                undefined,
                                "\ufffc"
                            );
                            // Get the last word (everything after last space)
                            const match = textBefore.match(/(\S+)$/);
                            const word = match ? match[1] : "";
                            // Only notify when the word actually changes to avoid
                            // infinite render loops (setOptions → updatePluginViews → setState → re-render)
                            if (word !== lastWord) {
                                lastWord = word;
                                onWordChange(word);
                            }
                        },
                    };
                },
            }),
        ];
    },

    addKeyboardShortcuts() {
        return {
            Enter: () => runHeadingEnterBehavior(this.editor),
            Tab: () => runSnippetTabBehavior(this.editor, this.options.snippets),
        };
    },
});

// ── Snippet Definitions ──────────────────────────────────────────────────────

// Helper: generate table content string for NxM table
function generateTableContent(rows: number, cols: number): string {
    const colSpec = "c".repeat(cols);
    let tableStr = `\\begin{tabular}{${colSpec}}\n`;
    tableStr += "\\hline\n";

    for (let r = 0; r < rows; r++) {
        const cells: string[] = [];
        for (let c = 0; c < cols; c++) {
            cells.push(`  `); // placeholder spaces for each cell
        }
        tableStr += cells.join(" & ");
        tableStr += " \\\\\n";
        if (r === 0) {
            tableStr += "\\hline\n"; // header separator
        }
    }
    tableStr += "\\hline\n";
    tableStr += "\\end{tabular}";
    return tableStr;
}

export function createLatexSnippets(): SnippetDefinition[] {
    return [
        // ── REGEX-BASED TRIGGERS (check first) ──────────────────────────────

        // Dynamic table: t<rows>b<cols>l (e.g., t3b5l = 3 rows, 5 cols)
        {
            trigger: "",
            handler: () => false,
            isRegex: true,
            regexPattern: "t(\\d+)b(\\d+)l",
            dynamicHandler: (editor, captures) => {
                const rows = parseInt(captures[0]) || 2;
                const cols = parseInt(captures[1]) || 2;
                const triggerLen = `t${captures[0]}b${captures[1]}l`.length;

                const { state } = editor.view;
                const { from } = state.selection;

                const tableContent = generateTableContent(rows, cols);

                // Calculate position of first cell: after \begin{tabular}{...}\n\hline\n
                // +1 for code block node offset
                const codeBlockStart = from - triggerLen + 1;
                const colSpec = "c".repeat(cols);
                const headerLen = `\\begin{tabular}{${colSpec}}\n\\hline\n`.length;
                const firstCellPos = codeBlockStart + headerLen;

                editor
                    .chain()
                    .deleteRange({ from: from - triggerLen, to: from })
                    .insertContent({
                        type: "codeBlock",
                        content: [{ type: "text", text: tableContent }],
                    })
                    .setTextSelection(firstCellPos)
                    .run();
                return true;
            },
        },

        // ── EXACT TRIGGERS ──────────────────────────────────────────────────

        // Simple tbl fallback (2x2 table)
        {
            trigger: "tbl",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                const tableContent = generateTableContent(2, 2);

                // Calculate position of first cell
                const codeBlockStart = from - 3 + 1;
                const headerLen = `\\begin{tabular}{cc}\n\\hline\n`.length;
                const firstCellPos = codeBlockStart + headerLen;

                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .insertContent({
                        type: "codeBlock",
                        content: [{ type: "text", text: tableContent }],
                    })
                    .setTextSelection(firstCellPos)
                    .run();
                return true;
            },
        },

        // Document metadata
        {
            trigger: "tl",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({ type: "docTitle", content: [{ type: "text", text: "" }] })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "aut",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .insertContent({ type: "docAuthor", content: [{ type: "text", text: "" }] })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "abs",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .insertContent({
                        type: "docAbstract",
                        content: [{ type: "paragraph" }],
                    })
                    .focus()
                    .run();
                return true;
            },
        },

        // Sections
        {
            trigger: "scn",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                const isEmpty = state.doc.textBetween(
                    state.selection.$from.start(),
                    state.selection.$from.end(),
                    "\n"
                ).trim() === "scn" || state.selection.$from.parent.textContent.trim() === "scn";

                if (isEmpty) {
                    // If paragraph only contains trigger, replace entire paragraph
                    editor
                        .chain()
                        .deleteRange({ from: from - 3, to: from })
                        .setNode("heading", { level: 1 })
                        .insertContent(" ")
                        .focus()
                        .run();
                } else {
                    // Otherwise just delete trigger and convert
                    editor
                        .chain()
                        .deleteRange({ from: from - 3, to: from })
                        .setNode("heading", { level: 1 })
                        .focus()
                        .run();
                }
                return true;
            },
        },
        {
            trigger: "sbn",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                const isEmpty = state.doc.textBetween(
                    state.selection.$from.start(),
                    state.selection.$from.end(),
                    "\n"
                ).trim() === "sbn" || state.selection.$from.parent.textContent.trim() === "sbn";

                if (isEmpty) {
                    editor
                        .chain()
                        .deleteRange({ from: from - 3, to: from })
                        .setNode("heading", { level: 2 })
                        .insertContent(" ")
                        .focus()
                        .run();
                } else {
                    editor
                        .chain()
                        .deleteRange({ from: from - 3, to: from })
                        .setNode("heading", { level: 2 })
                        .focus()
                        .run();
                }
                return true;
            },
        },
        {
            trigger: "ssn",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                const isEmpty = state.doc.textBetween(
                    state.selection.$from.start(),
                    state.selection.$from.end(),
                    "\n"
                ).trim() === "ssn" || state.selection.$from.parent.textContent.trim() === "ssn";

                if (isEmpty) {
                    editor
                        .chain()
                        .deleteRange({ from: from - 3, to: from })
                        .setNode("heading", { level: 3 })
                        .insertContent(" ")
                        .focus()
                        .run();
                } else {
                    editor
                        .chain()
                        .deleteRange({ from: from - 3, to: from })
                        .setNode("heading", { level: 3 })
                        .focus()
                        .run();
                }
                return true;
            },
        },

        // Theorem environments
        {
            trigger: "oo",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "theorem", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "ll",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "lemma", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "ps",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "proposition", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "cc",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "corollary", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "dd",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "definition", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "re",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "remark", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "ee",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "example", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "pf",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "proof", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "ex",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "exercise", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },

        // Generic environment
        {
            trigger: "en",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({
                        type: "genericEnv",
                        attrs: { envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },

        // Math environments
        {
            trigger: "mm",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({ type: "inlineMath", attrs: { latex: "" } })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "mk",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({ type: "inlineMath", attrs: { latex: "" } })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "dm",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                const { $from } = state.selection;

                // Check if there's content before cursor in current paragraph
                const textBefore = $from.parent.textContent.substring(0, $from.parentOffset);
                const hasContentBefore = textBefore.replace("dm", "").trim().length > 0;

                if (hasContentBefore) {
                    // Insert paragraph break, then display math
                    editor
                        .chain()
                        .deleteRange({ from: from - 2, to: from })
                        .insertContent([{ type: "paragraph" }])
                        .insertContent({ type: "displayMath", attrs: { latex: "" } })
                        .focus()
                        .run();
                } else {
                    // Just replace with display math
                    editor
                        .chain()
                        .deleteRange({ from: from - 2, to: from })
                        .insertContent({ type: "displayMath", attrs: { latex: "" } })
                        .focus()
                        .run();
                }
                return true;
            },
        },

        // Lists
        {
            trigger: "tz",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .toggleBulletList()
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "enn",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .toggleOrderedList()
                    .focus()
                    .run();
                return true;
            },
        },

        // Enumerate variants (enumitem styles)
        {
            trigger: "enI",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .insertContent({
                        type: "orderedList",
                        attrs: { listStyle: "upper-roman" },
                        content: [{ type: "listItem", content: [{ type: "paragraph" }] }],
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "eni",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .insertContent({
                        type: "orderedList",
                        attrs: { listStyle: "lower-roman" },
                        content: [{ type: "listItem", content: [{ type: "paragraph" }] }],
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "enA",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .insertContent({
                        type: "orderedList",
                        attrs: { listStyle: "upper-alpha" },
                        content: [{ type: "listItem", content: [{ type: "paragraph" }] }],
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "ena",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .insertContent({
                        type: "orderedList",
                        attrs: { listStyle: "lower-alpha" },
                        content: [{ type: "listItem", content: [{ type: "paragraph" }] }],
                    })
                    .focus()
                    .run();
                return true;
            },
        },

        // New item
        {
            trigger: "tm",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .splitListItem("listItem")
                    .focus()
                    .run();
                return true;
            },
        },

        // Verbatim / code block
        {
            trigger: "vb",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .setCodeBlock()
                    .focus()
                    .run();
                return true;
            },
        },

        // Inline code / teletype (tt)
        {
            trigger: "tt",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .setMark("code")
                    .focus()
                    .run();
                return true;
            },
        },

        // ── Long-form triggers (matching document-snippets.ts) ──

        // Theorem environments (long form)
        {
            trigger: "thm",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "theorem", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "lem",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "lemma", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "prop",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 4, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "proposition", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "cor",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "corollary", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "defn",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 4, to: from })
                    .insertContent({
                        type: "theoremBlock",
                        attrs: { envType: "definition", envName: "" },
                        content: [{ type: "paragraph" }],
                    })
                    .command(({ tr, dispatch }) => {
                        if (dispatch) {
                            const safe = Math.min(from, tr.doc.content.size - 1);
                            tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
                        }
                        return true;
                    })
                    .focus()
                    .run();
                return true;
            },
        },

        // Subsection/subsubsection headings — sssec MUST come before ssec which MUST come before sec
        // so the longer trigger matches first (Tab handler is first-match-wins)
        {
            trigger: "sssec",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 5, to: from })
                    .setNode("heading", { level: 3 })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "ssec",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 4, to: from })
                    .setNode("heading", { level: 2 })
                    .focus()
                    .run();
                return true;
            },
        },
        // Section alias — comes AFTER sssec/ssec so those match first
        {
            trigger: "sec",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .setNode("heading", { level: 1 })
                    .focus()
                    .run();
                return true;
            },
        },

        // Math environments
        {
            trigger: "eq",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 2, to: from })
                    .insertContent({ type: "numberedEquation", attrs: { latex: "" } })
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "ali",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .insertContent({
                        type: "genericEnv",
                        attrs: { envName: "align" },
                        content: [{ type: "paragraph" }],
                    })
                    .focus()
                    .run();
                return true;
            },
        },

        // Lists (long form)
        {
            trigger: "enum",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 4, to: from })
                    .toggleOrderedList()
                    .focus()
                    .run();
                return true;
            },
        },
        {
            trigger: "item",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 4, to: from })
                    .toggleBulletList()
                    .focus()
                    .run();
                return true;
            },
        },

        // Figure
        {
            trigger: "fig",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 3, to: from })
                    .insertContent({
                        type: "figureBlock",
                        attrs: { src: "", caption: "", label: "", width: "0.8", filename: "" },
                    })
                    .focus()
                    .run();
                return true;
            },
        },

        // TikZ picture
        {
            trigger: "tikz",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 4, to: from })
                    .insertContent({
                        type: "tikzBlock",
                        attrs: { source: "" },
                    })
                    .focus()
                    .run();
                return true;
            },
        },

        // Code block alias
        {
            trigger: "code",
            handler: (editor) => {
                const { state } = editor.view;
                const { from } = state.selection;
                editor
                    .chain()
                    .deleteRange({ from: from - 4, to: from })
                    .setCodeBlock()
                    .focus()
                    .run();
                return true;
            },
        },

    ];
}
